/**
 * Main tournament mode container component.
 * Orchestrates group stage, bracket stage, and results.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, LayoutGrid, Play, Swords, Trophy } from 'lucide-react';
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BracketStage } from './BracketStage';
import { FilterStage } from './FilterStage';
import { GroupStage } from './GroupStage';
import { TournamentOverview } from './TournamentOverview';
import { TournamentProgress } from './TournamentProgress';
import { useTournament } from './TournamentProvider';
import { TournamentResults } from './TournamentResults';

type ViewMode = 'current' | 'overview';

export function TournamentMode() {
  const { state, actions } = useTournament();
  const [viewMode, setViewMode] = useState<ViewMode>('current');

  // Not started state - show start button
  if (!state) {
    return (
      <div className="h-full flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Trophy className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Tournament Mode</CardTitle>
            <CardDescription className="text-base">
              Rate countries by competing them in a tournament bracket. Rank
              groups of countries, then pick winners in head-to-head matchups.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-lg">1.</span>
                <p>
                  <strong>Group Stage:</strong> Rank groups of 5 countries. Top
                  2 from each group advance.
                </p>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-lg">2.</span>
                <p>
                  <strong>Knockout Stage:</strong> Pick winners in head-to-head
                  matchups until a champion emerges.
                </p>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-lg">3.</span>
                <p>
                  <strong>Final Ratings:</strong> Countries get 1-5 ratings
                  based on how far they advanced.
                </p>
              </div>
            </div>
            <Button
              onClick={actions.startTournament}
              size="lg"
              className="w-full font-semibold"
            >
              <Play className="w-5 h-5 mr-2" />
              Start Tournament
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // When tournament is completed, render results without wrapper padding
  if (state.status === 'completed') {
    return (
      <div className="h-full overflow-hidden">
        <TournamentResults />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col gap-3 p-4">
      {/* Header with progress and view toggle */}
      <div className="shrink-0 flex items-center gap-3">
        <div className="flex-1">
          <TournamentProgress />
        </div>
        {/* Hide tabs during filter stage (no overview yet) */}
        {state.status !== 'filter_stage' && (
          <Tabs
            value={viewMode}
            onValueChange={(v) => setViewMode(v as ViewMode)}
          >
            <TabsList className="h-8">
              <TabsTrigger value="current" className="text-xs px-2 h-6">
                <Swords className="w-3.5 h-3.5 mr-1" />
                Play
              </TabsTrigger>
              <TabsTrigger value="overview" className="text-xs px-2 h-6">
                <LayoutGrid className="w-3.5 h-3.5 mr-1" />
                Overview
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>

      {/* Main content area */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <AnimatePresence mode="wait">
          {viewMode === 'overview' ? (
            <motion.div
              key="overview"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="h-full overflow-hidden"
            >
              <TournamentOverview />
            </motion.div>
          ) : (
            <>
              {state.status === 'filter_stage' && (
                <motion.div
                  key="filter-stage"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="h-full overflow-hidden"
                >
                  <FilterStage />
                </motion.div>
              )}

              {state.status === 'group_stage' && (
                <motion.div
                  key="group-stage"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="h-full overflow-hidden"
                >
                  <GroupStage />
                </motion.div>
              )}

              {state.status === 'bracket_stage' && (
                <motion.div
                  key="bracket-stage"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="h-full overflow-hidden"
                >
                  <BracketStage />
                </motion.div>
              )}
            </>
          )}
        </AnimatePresence>
      </div>

      {/* Abandon button */}
      <div className="shrink-0 flex justify-center">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-destructive"
            >
              <AlertCircle className="w-4 h-4 mr-1.5" />
              Abandon Tournament
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Abandon tournament?</AlertDialogTitle>
              <AlertDialogDescription>
                This will discard all your progress in this tournament. You can
                start a new one anytime.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep Playing</AlertDialogCancel>
              <AlertDialogAction
                onClick={actions.abandonTournament}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Abandon
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
